import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { PhoneKeypadIcon, PhoneMicrophoneIcon, PhoneMicrophoneMutedIcon, selectCall, webrtcActions } from 'webrtc-client-janus';
import * as Phone from '../api/call-control';
import { useAlarm, useIsSoftphoneEnabled } from '../app-hooks';
import { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { BasicNotification, CallDevice, CallDeviceType, CallState, ClidObjectType, Command, DeviceType, ExtensionDevice, Identifier, Media, MediaType, QueueIconEnum, QueueStats, Region, CallType } from '../model';
import { telephoneNumberToRegional } from '../phone-utils';
import { actions } from '../store';
import { getActiveDevice } from '../store/reducers';
import { selectCommands } from '../store/reducers/command';
import { MyDevices, selectInstantMessagerSettings, selectMyAccess, selectMyData, selectMyDevices, selectMyRegion, selectMyself, selectCallNotificationsShowOnIncoming, selectCallNotificationsShowOnOutgoing, selectShowQueueAlerts } from '../store/reducers/config';
import { getInstantMessageNotification, InstantMessagerNotification } from '../store/reducers/instantmessager';
import { getParkedMedia, selectInIpfxClient } from '../store/reducers/localSettings';
import { getWrapupNotification, selectCallAssisantMedia, selectHeldCall, selectMyActiveCalls, selectMyCalls, selectQueueHeldCall } from '../store/reducers/media';
import { selectExtension, selectExtensionDetails } from '../store/reducers/queueAgents';
import { selectMyQueueAlerts } from '../store/reducers/queues';
import { getJitsi } from '../store/reducers/settings';
import { selectServerNotifcations } from '../store/reducers/toasts';
import Button from './Button';
import './CallPopup.scss';
import { RelativeShortFormatDateTime } from './CallsPage';
import { ReactComponent as AllowRecordIcon } from './icons/callcontrol/allow-record.svg';
import { ReactComponent as AnswerIcon } from './icons/callcontrol/answer.svg';
import { ReactComponent as BlindTransferIcon } from './icons/callcontrol/blind-transfer.svg';
import { ReactComponent as CancelTransferIcon } from './icons/callcontrol/cancel-transfer.svg';
import { ReactComponent as CompleteTransferIcon } from './icons/callcontrol/complete-transfer.svg';
import { ReactComponent as ConferenceIcon } from './icons/callcontrol/conference.svg';
import { ReactComponent as Dtmf0Icon } from './icons/callcontrol/dtmf-0.svg';
import { ReactComponent as Dtmf1Icon } from './icons/callcontrol/dtmf-1.svg';
import { ReactComponent as Dtmf2Icon } from './icons/callcontrol/dtmf-2.svg';
import { ReactComponent as Dtmf3Icon } from './icons/callcontrol/dtmf-3.svg';
import { ReactComponent as Dtmf4Icon } from './icons/callcontrol/dtmf-4.svg';
import { ReactComponent as Dtmf5Icon } from './icons/callcontrol/dtmf-5.svg';
import { ReactComponent as Dtmf6Icon } from './icons/callcontrol/dtmf-6.svg';
import { ReactComponent as Dtmf7Icon } from './icons/callcontrol/dtmf-7.svg';
import { ReactComponent as Dtmf8Icon } from './icons/callcontrol/dtmf-8.svg';
import { ReactComponent as Dtmf9Icon } from './icons/callcontrol/dtmf-9.svg';
import { ReactComponent as DtmfHarshIcon } from './icons/callcontrol/dtmf-hash.svg';
import { ReactComponent as DtmfStarIcon } from './icons/callcontrol/dtmf-star.svg';
import { ReactComponent as ForwardToToAssociateIcon } from './icons/callcontrol/forward-associate.svg';
import { ReactComponent as ForwardToToMobileIcon } from './icons/callcontrol/forward-mobile.svg';
import { ReactComponent as ForwardToVoicemailIcon } from './icons/callcontrol/forward-voicemail.svg';
import { ReactComponent as HangupIcon } from './icons/callcontrol/hangup.svg';
import { ReactComponent as HoldAndRetirveIcon } from './icons/callcontrol/hold-and-retrieve.svg';
import { ReactComponent as ParkForIcon } from './icons/callcontrol/hold-for.svg';
import { ReactComponent as HoldIcon } from './icons/callcontrol/hold.svg';
import { ReactComponent as HoldMessageIcon } from './icons/callcontrol/hold-message.svg';
import { ReactComponent as ListenVoicemailIcon } from './icons/callcontrol/listen-voicemail.svg';
import { ReactComponent as ParkIcon } from './icons/callcontrol/queue-hold.svg';
import { ReactComponent as QueueResumeIcon } from './icons/callcontrol/queue-resume.svg';
import { ReactComponent as RecordIcon } from './icons/callcontrol/record.svg';
import { ReactComponent as ResumeIcon } from './icons/callcontrol/resume.svg';
import { ReactComponent as RetrieveVoicemailIcon } from './icons/callcontrol/retrieve-voicemaill.svg';
import { ReactComponent as StopRecordIcon } from './icons/callcontrol/stop-record.svg';
import { ReactComponent as SupervisedTransferIcon } from './icons/callcontrol/supervised-transfer.svg';
import { ReactComponent as SuppressRecordIcon } from './icons/callcontrol/suppress-record.svg';
import { ReactComponent as WrapupIcon } from './icons/callstatus/wrapup.svg';
import { ReactComponent as CloseIcon } from './icons/cross.svg';
import { ReactComponent as AnswerHomeIcon } from './icons/dial-home.svg';
import { ReactComponent as AnswerMobileIcon } from './icons/dial-mobile.svg';
import { ReactComponent as AnswerOtherIcon } from './icons/dial-other.svg';
import { ReactComponent as AnswerSoftPhoneIcon } from './icons/dial-softphone.svg';
import { ReactComponent as FlagIcon } from './icons/flag.svg';
import { ReactComponent as InstantMessagerNormalIcon } from './icons/instantmessager/chat.svg';
import { ReactComponent as InstantMessagerUrgentIcon } from './icons/instantmessager/chat_urgent.svg';
import { ReactComponent as MiniseIcon } from './icons/minimize.svg';
import { GetCallControl } from './MainToolbarDialButton';
import MediaControl from './MediaControl';
import MediaIcon from './MediaIcon';
import { MenuItem } from './Menu';
import ProfilePhoto from './ProfilePhoto';
import QueueIcon from './QueueIcon';
import SplitButton from './SplitButton';
import Timer from './Timer';
import ToastPortal from './ToasterPortal';
import App from '../App';

interface Props extends HasClassName {
}

export default function CallPopup({ className }: Props) {
  return <div key='CallPopupContainer' className={classNames('CallPopupContainer', className)}>
    <InstantMessageNotifications />
    <WrapupNotifications />
    <ServerNotifications />
    <CommandNotifications />
    <QueueAlerts />
    <MediaNotifications />
  </div>
}

function QueueAlerts() {
  const alerts = useSelector(selectMyQueueAlerts)
  const inIpfxClient = useSelector(selectInIpfxClient)
  const showQueueAlert = useSelector(selectShowQueueAlerts)

  if (!showQueueAlert) {
    return null
  }
  
  //AR*** Keep track of this queue's alert being manually closed so we do
  
  return <>
    {alerts.map(a => {
      if (a) {
        if (inIpfxClient) {
          return <ToastPortal key={'tp-' + a.id} id={'tp-' + a.id} toastName={'alert'} width={380} height={75} className='CallPopupContainer' isQueueCall={false}>
            <QueueAlert key={'tp-' + a.id} queue={a} />
          </ToastPortal>
        } else {
          return <QueueAlert key={'tp-' + a.id} queue={a} />
        }
      }
      return null
    })}
  </>
}

function CommandNotifications() {
  const commands = useSelector(selectCommands)
  const inIpfxClient = useSelector(selectInIpfxClient)
  return <>
    {commands.filter(x => x.Hide === false).map(command => {
      if (inIpfxClient) {
        return <ToastPortal key={'tp-' + getCommandIdFromCommand(command)} id={getCommandIdFromCommand(command)} toastName={'Incoming ' + getMediaTypeDescriptionFromCommand(command)}
          width={380} height={145} className='CallPopupContainer' isQueueCall={false}>
          <CommandPopup key={getCommandIdFromCommand(command)} command={command} />
        </ToastPortal>
      } else {
        return <CommandPopup key={getCommandIdFromCommand(command)} command={command} />
      }
    })}
  </>
}

function ServerNotifications() {
  const notifications = useSelector(selectServerNotifcations)
  const inIpfxClient = useSelector(selectInIpfxClient)
  return <>
    {Object.entries(notifications).map(([id, notification]) => {
      if (inIpfxClient) {
        return <ToastPortal key={'tp-' + id} id={id} toastName={notification.subject} width={380} height={75} className='CallPopupContainer' isQueueCall={false}>
          <ServerNotification key={id} id={id} notification={notification} />
        </ToastPortal>
      } else {
        return <ServerNotification key={id} id={id} notification={notification} />
      }
    })}
  </>
}

function InstantMessageNotifications() {
  const settings = useSelector(selectInstantMessagerSettings)
  const instantmMssageNotification = useSelector(getInstantMessageNotification)
  const inIpfxClient = useSelector(selectInIpfxClient)

  const key = 'd63db82d-675b-4db4-9faa-766cb0adb3d8'
  if (!settings) { return null }

  if (settings && settings.imEnabled && instantmMssageNotification.unread > 0 && instantmMssageNotification.conversationId !== '') {
    if (inIpfxClient) {
      return <ToastPortal key={'tp-' + key} id={key} toastName={instantmMssageNotification.title}
        width={380} height={60} className='CallPopupContainer' isQueueCall={false}>
        <ImNotification notification={instantmMssageNotification} />
      </ToastPortal>
    }
    return <ImNotification notification={instantmMssageNotification} />
  }
  return null
}

function WrapupNotifications() {
  const dispatch = useDispatch()
  const notifcation = useSelector(getWrapupNotification)
  const inIpfxClient = useSelector(selectInIpfxClient)
  if (notifcation.minimise > 0) {
    const t = dayjs(notifcation.minimise)
    const now = dayjs(Date.now())
    if (t <= now) {
      dispatch(actions.hideWrapupNotification(0))
    } else {
      const timeout = t.diff(now, 'millisecond')

      setTimeout(() => {
        dispatch(actions.hideWrapupNotification(0))
      }, timeout);
    }
    return null;
  }

  const key = '0cc18341-33e5-4160-b3ca-4b63afef6041'

  if (notifcation.media.length > 0) {
    if (inIpfxClient) {
      return <ToastPortal key={'tp-' + key} id={key} toastName='Wrapups waiting'
        width={380} height={75} className='CallPopupContainer' isQueueCall={false}>
        <WrapupNotification mediaId={notifcation.media[0]} length={notifcation.media.length} updated={notifcation.updated} />
      </ToastPortal>
    }
    return <WrapupNotification mediaId={notifcation.media[0]} length={notifcation.media.length} updated={notifcation.updated} />
  }
  return null
}

function MediaNotifications() {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself)
  const devices = useSelector(selectMyDevices)
  const calls = useSelector(selectMyActiveCalls)
  const region = useSelector(selectMyRegion)
  const popOnIncoming = useSelector(selectCallNotificationsShowOnIncoming)
  const popOnOutgoing = useSelector(selectCallNotificationsShowOnOutgoing)

  const inIpfxClient = useSelector(selectInIpfxClient)
  const assistants = useSelector(s => {
    const access = selectMyAccess(s)
    //remove data if user level feature is not enabled
    return access && access.features.alertCallAssistant ? selectCallAssisantMedia(s) : null
  }) || []

  if (!myId || !devices || !region || !calls) { return null }
  return <>
    {[...assistants, ...calls].filter(x => x.hide === false && x.status !== CallState.WaitingForWrapup).sort((a, b) => b.created - a.created).map(call => {
      if (call.minimised !== 0) {
        const t = dayjs(call.minimised)
        const now = dayjs(Date.now())
        if (t <= now) {
          dispatch(actions.hideCall(call.id, 0))
        } else {
          const timeout = t.diff(now, 'millisecond')

          setTimeout(() => {
            dispatch(actions.hideCall(call.id, 0))
          }, timeout);
        }
        return null;
      }
      
      if (!shouldRenderByPopSetting(call, popOnIncoming, popOnOutgoing)) {
        return null;
      }

      if (inIpfxClient) {
        return <ToastPortal key={'tp-' + call.storeId} id={call.storeId} toastName={callStateDescription(call, myId, getActiveDevice(call, devices.devices, devices.primaryDevice))}
          width={380} height={96} className='CallPopupContainer' isQueueCall={true}>
          <MediaNotification myId={myId} call={call} devices={devices} region={region} isCallAssistant={assistants.find(m => m.id === call.id) !== undefined} minimise={true} />
        </ToastPortal>
      } else {
        return <MediaNotification key={call.id} myId={myId} call={call} devices={devices} region={region} isCallAssistant={assistants.find(m => m.id === call.id) !== undefined} minimise={true} />
      }
    })}

  </>
}

function MediaNotification({ myId, call, devices, region, isCallAssistant, minimise }: { myId: Identifier, call: Media, devices: MyDevices, region: Region, isCallAssistant: boolean, minimise?: boolean }) {
  const dispatch = useDispatch()
  const description = callClidDescription(call, region)
  const details = callStateDescription(call, myId, getActiveDevice(call, devices.devices, devices.primaryDevice))


  return <React.Fragment>
    <div className='Media popup' key={call.id}>
      <div className={classNames('card', isCallAssistant && 'assistant', minimise && 'minimise')} onClick={() => dispatch(actions.openAgentView(call.id, call.status === CallState.WaitingForWrapup ? 'wrapups' : ''))}>
        <ProfilePhoto className='photo' clid={call.clid} name={call.clidDescription} load={call.clidObjectKey !== ''} />
        <div title={description}>{description}</div>
        <div>
          <CloseIcon xlinkTitle='Hide' className='close ' onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            dispatch(actions.hideCall(call.id))

          }} />
          {minimise && <MiniseIcon xlinkTitle='Minise' className='minimise-icon' onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            dispatch(actions.hideCall(call.id, 10))

          }} />}
        </div>
        <div className='details' title={details}>{details}</div>
        <div className='time'>
          {call.status !== CallState.None && <Timer ts={call.elapsedTime} />}
        </div>
      </div>
      <CallControl className="call-toolbar" call={call} />
    </div>
  </React.Fragment>
}

const shouldRenderByPopSetting = (
  call: Media,
  popOnIncoming: boolean,
  popOnOutgoing: boolean
) => {
  if (call.mediaType !== 'CALL') return true
  if (call.callType === CallType.Incoming || call.callType === CallType.Queue) return popOnIncoming
  if (call.callType === CallType.Outgoing) return popOnOutgoing
  return true
}

function WrapupNotification({ mediaId, length, updated }: { mediaId: string, length: number, updated: number }) {
  const dispatch = useDispatch()
  return <div className='notification popup' style={{ cursor: 'pointer' }} onClick={() => dispatch(actions.openAgentView(mediaId, 'wrapups'))}>
    <div className='image'><WrapupIcon /></div>
    <div className='title'></div>
    <div><MiniseIcon xlinkTitle='Minise' className='minimise-icon' onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      dispatch(actions.hideWrapupNotification(10))

    }} /><CloseIcon className='close' onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      dispatch(actions.hideWrapupNotification(120))
    }} /></div>
    <div className='body'>There {length > 1 ? 'are' : 'is'} {length} item{length > 1 ? 's' : ''} requiring attention.</div>
    <div className='footer'><div><RelativeShortFormatDateTime date={updated} /></div></div>
  </div>
}

function ImNotification({ notification }: { notification: InstantMessagerNotification }) {
  const dispatch = useDispatch()
  return <div className='popup' onClick={() => dispatch(actions.changeInstantmessageView(notification.conversationId))}>
    <div className='card'>
      <div className='ProfilePhoto photo'>{notification.urgent ? <InstantMessagerUrgentIcon /> : <InstantMessagerNormalIcon />}</div>
      <div title={notification.title}>{notification.unread + ' Unread Conversation' + (notification.unread === 1 ? '' : 's')}</div>
      <div><CloseIcon className='close' onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        dispatch(actions.instantMessageNotifcationHide(true))
      }} /></div>
      <div title={notification.message}>Last Message : {notification.message}</div>
    </div>
  </div>
}

function ServerNotification({ id, notification }: { id: string, notification: BasicNotification }) {
  const dispatch = useDispatch()
  const footer = notification.status === '' ? <RelativeShortFormatDateTime date={notification.time} /> : <>{notification.status}</>
  return <div className='notification popup'>
    <div className='image'>{PupupAlert(notification.imageKey)}</div>
    <div className='title'>{notification.subject}</div>
    <div className='close' ><CloseIcon onClick={(e) => {
      dispatch(actions.closeServertNotification(id))
    }} /></div>
    <div className='body'>{notification.alertBody}</div>
    <div className='footer'><div>{footer}</div></div>
  </div>
}

function QueueAlert({ queue }: { queue: QueueStats }) {
  const dispatch = useDispatch()
  return <div className="QueueAlert popup">
    <div><QueueIcon icon={queue.toaster} /></div>
    <div className="description">
      <div>Queue {queue.id} - {queue.toaster === QueueIconEnum.OpenWarning ? 'Warning' : queue.toaster === QueueIconEnum.OpenAlert ? 'Alert' : ''}</div>
      <div className='close' ><CloseIcon onClick={(e) => {
        dispatch(actions.closeAlertQueue(queue))
        //AR*** Keep track of this queue's alert being manually closed so we don't refresh it again until the alert has been cleared by the server
      }} /></div>
    </div>
    <div>{queue.description}</div>
    <div>Calls={queue.count}, GOS={queue.agentStatus}%</div>
  </div>
}

function PupupAlert(id: number) {
  switch (id) {
    case 1329:
      {
        return <FlagIcon fill='red' />
      }
  }
  return <></>
}

function CommandPopup({ command }: { command: Command }) {
  const dispatch = useDispatch()
  const id = getCommandIdFromCommand(command)
  const callId = command.ExtendedInfo.QueueItems ? command.ExtendedInfo.QueueItems.Key : '';
  const onDecline = useCallback(() => {
    dispatch(actions.declineCommand(id))
  }, [dispatch, id])

  const onHide = useCallback(() => {
    dispatch(actions.removeCommand(id))
  }, [dispatch, id])

  const init = getInitFromCommand(command)
  const timeout = init ? init.Timeout : 10
  useAlarm(command.Time + timeout * 1000, onHide)

  if (!command.ExtendedInfo.QueueItems || !init) return null

  const itemType = getMediaTypeFromCommand(command)
  const type = getMediaTypeDescriptionFromCommand(command)

  const details = command.ExtendedInfo.QueueItems.DialledNumberDescription + ' [' + command.ExtendedInfo.QueueItems.QueueNumber + ']'
  return <div className='popup'>
    <div className='card'>
      <MediaIcon className='media' mediaType={itemType} />
      <div>Incoming {type}</div>
      <div><CloseIcon className='close' onClick={onDecline} /></div>
      <div title={details}>{details}</div>
      <div className='time'>
        <Timer reverse={timeout !== undefined} ts={command.Time + (timeout || 0) * 1000} />
      </div>
    </div>
    <div className="media-toolbar">
      <div>{command.ExtendedInfo.QueueItems.ItemType === 'OUT' ? 'To' : 'From'}:</div>
      <div>{command.ExtendedInfo.QueueItems.ContactDescription} </div>
      <div>Subject:</div>
      <div>{command.ExtendedInfo.QueueItems.Subject}</div>
      <div className="buttons">
        {init.CanDecline === "1" && <Button type='button' onClick={onDecline}>Decline</Button>}
        <Button primary onClick={() => dispatch(actions.acceptCommand(id, callId))}>Accept</Button>
      </div>
    </div>
  </div>
}

export function getInitFromCommand(c: Command) {
  return c.ExtendedInfo.PPInitialise ? c.ExtendedInfo.PPInitialise : c.ExtendedInfo.PPINITIALISE ? c.ExtendedInfo.PPINITIALISE : null
}

export function getCommandIdFromCommand(c: Command) {
  return c.ExtendedInfo.KEYS ? c.ExtendedInfo.KEYS.DSLKey : c.ExtendedInfo.Keys ? c.ExtendedInfo.Keys.DSLKey ? c.ExtendedInfo.Keys.DSLKey : c.ExtendedInfo.Keys.SessionID : '';
}

export function getMediaTypeFromCommand(c: Command) {
  return c.ExtendedInfo.KEYS ? c.ExtendedInfo.KEYS.ItemType : c.ExtendedInfo.Keys && c.ExtendedInfo.QueueItems ? c.ExtendedInfo.QueueItems.ItemType : ''
}

export function getMediaTypeDescriptionFromCommand(c: Command) {
  return getMediaTypeDescription(getMediaTypeFromCommand(c))
}

function getMediaTypeDescription(itemType: MediaType) {
  let type = ''
  switch (itemType) {
    case 'EMAIL':
      type = 'Email'
      break
    case 'TC':
      type = 'Web Chat'
      break
    case 'FB':
      type = 'Facebook'
      break;
    case 'OUT':
      type = 'Outbound'
      break;
    case 'TWEET':
      type = 'Tweet'
      break;
  }
  return type
}

export function callClidDescription(call: Media, region: Region | null): string {
  return ((call.clidDescription === '') ? telephoneNumberToRegional(region, call.clid) : (call.clid === '') ? call.clidDescription : ((call.clidDescription !== call.clid) ? call.clidDescription + ' (' + telephoneNumberToRegional(region, call.clid) + ')' : telephoneNumberToRegional(region, call.clid))) + (call.clidAccountCode !== '' ? ' a/c: ' + call.clidAccountCode : '')
}

export function callStateDescription(call: Media, myId: Identifier, callDevice?: ExtensionDevice): string {
  let text = ""
  if ((call.mediaType === "CALL" || call.mediaType === "VM") && callDevice) {
    text = getCallStatus(call.status) + ' on ';
    let device = getDeviceType(callDevice.baseType) + ' Phone' + ((callDevice.baseType === DeviceType.Softphone) ? ' (' + callDevice.description + ')' : '')
    if (call.forwardFrom !== '') {
      text = 'From ' + call.forwardFrom + ' on ' + device;
      //todo need to request extension data command = 'entity-extension'
    } else {
      if (call.status === CallState.CallInVoicemail) {
        text = 'In Voicemail';

        if (call.queueId !== myId.id) {


          text = 'Call for ' + call.queueIdDescription + ' [' + call.queueId + '] - In Voicemail';

          if (call.urgency === 1) {
            text = 'Urgent ' + text;
          }
          return text;
        }

        if (call.urgency === 1) {
          text = 'Urgent call in Voicemail'
        }

        return text;

      } else if (call.status === CallState.QueueHold || call.status === CallState.Queueing) {
        text = 'Parked'

      } else {
        text += device
      }

      if (call.isQueueCall) {
        text = "Queue '" + call.dnisDescription + ((call.devices && call.devices.length > 1) ? "" : "' on " + device)

        if (call.status === CallState.WaitingForWrapup) {
          text = "Queue '" + call.dnisDescription + "' is waiting in wrapup"
        }
      }

      if (call.extensionId !== myId.id || (call.forwardFrom !== '' && call.deviceDescription !== '')) {
        let name = ((call.forwardFrom !== '') ? call.deviceDescription + ' (' + call.forwardFrom + ') ' : call.deviceDescription)

        if (name !== '') {
          name += ' - '
        }

        if (call.isQueueCall) {
          text = name + "Queue '" + call.dnisDescription + ((call.devices && call.devices.length > 1) ? "" : "' on " + device)

          if (call.status === CallState.WaitingForWrapup) {
            text = "Queue '" + call.dnisDescription + "' is waiting in wrapup"
          }

        } else {
          if (call.status !== CallState.QueueHold && call.status !== CallState.Queueing) {
            text = 'Call for ' + call.queueIdDescription + ' [' + call.queueId + ']';
          }
        }
      }
    }
  } else {
    //should be a none queue media item
    text = "Queue '" + call.dnisDescription + "'"

    if (call.status === CallState.WaitingForWrapup) {
      text = "Queue '" + call.dnisDescription + "' is waiting in wrapup"
    }
  }
  return text
}

export function getDeviceType(type: DeviceType): string {
  switch (type) {
    case DeviceType.Home:
      return 'Home'
    case DeviceType.Mobile:
      return 'Mobile'
    case DeviceType.Other:
      return 'Other'
    case DeviceType.Softphone:
      return 'Softphone'
    case DeviceType.None:
    case DeviceType.Extension:
    default:
      return 'Desk'
  }
}

export function getCallStatus(status: CallState): string {
  switch (status) {
    case CallState.Unknown:
      return 'Unknown'
    case CallState.AnnouncementPending:
      return 'Pending'
    case CallState.AnnouncementAnswered:
      return 'Answered'
    case CallState.AnnouncementReturn:
      return 'Return'
    case CallState.ExtensionPending:
      return 'Ringing'
    case CallState.ExtensionAnswered:
      return 'Answered'
    case CallState.ExtensionReleased:
      return 'Released'
    case CallState.ExtensionReturn:
      return 'Answered'
    case CallState.Queueing:
      return 'Queuing'
    case CallState.Callback:
      return 'Callback'
    case CallState.VmHandled:
      return 'VM Handled'
    case CallState.ExtensionTransfer:
      return 'Transfer'
    case CallState.CallInVoicemail:
      return 'In Voicemail'
    case CallState.ExtensionTransferAnswer:
      return 'Transfer Answer'
    case CallState.CallbackRecording:
      return 'Recording'
    case CallState.Unattended:
      return 'Diverted'
    case CallState.UnattendedPending:
      return 'Pending'
    case CallState.UnattendedProcessing:
      return 'Processing'
    case CallState.QueueHold:
      return 'Parked'
    case CallState.PendinginQueue:
      return 'Pending'
    case CallState.WaitingForWrapup:
      return 'Wrapup'
    case CallState.Pending:
      return 'Pending'
    case CallState.Dialling:
      return 'Dialing'
    case CallState.Connected:
      return 'Connected'
    case CallState.OutboundRinging:
      return 'Dialled'
    case CallState.OutboundOffHook:
      return 'Ringing'
    case CallState.Hold:
      return 'Hold'
    case CallState.ConferenceCall:
      return 'Conference Call'
    case CallState.None:
      return ''
    default:
      return 'Unknown'
  }
}

function CallDeviceIcon({ device }: { device: CallDevice }) {
  switch (device.baseType) {
    case CallDeviceType.Home:
      return <AnswerHomeIcon />
    case CallDeviceType.Mobile:
      return <AnswerMobileIcon />
    case CallDeviceType.Other:
      return <AnswerOtherIcon />
    case CallDeviceType.Softphone:
      return <AnswerSoftPhoneIcon />
    default:
      console.error('Unknown icon ' + device.baseType);
      return <></>
  }
}

function getCallDevice(type: CallDeviceType) {
  switch (type) {

    case CallDeviceType.Home:
      return 'Home'
    case CallDeviceType.Mobile:
      return 'Mobile'
    case CallDeviceType.Other:
      return 'Other'
    case CallDeviceType.Softphone:
      return 'Softphone'
    case CallDeviceType.Extension:
    default:
      return 'Desk'
  }
}

function getCallDeviceDescription(device: CallDevice): string {
  let text = getCallDevice(device.baseType)
  if (device.baseType === CallDeviceType.Softphone) {
    text += " (" + device.description + ")"
  }
  return text
}


export function activeCallCount(myCalls: Media[], myDevices: ExtensionDevice[]): number {
  let count: number = 0
  for (let i = 0; i < myCalls.length; i++) {
    if (myDevices.find(x => x.number === myCalls[i].deviceId)) {
      if (myCalls[i].status !== CallState.WaitingForWrapup && myCalls[i].status !== CallState.None) {
        count++;
      }
    }
  }
  return count
}

export function CallControl({ call, className }: { call: Media } & HasClassName) {
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const access = useSelector(selectMyAccess)
  const devices = useSelector(selectMyDevices)
  const me = useSelector(selectMyData)

  const myExtensionDetails = useSelector(s => myId && selectExtensionDetails(s, myId))
  const clidExtensionDetails = useSelector(s => selectExtensionDetails(s, { id: call.clidObjectKey, server: call.server }))
  const calls = useSelector(selectMyCalls)
  const heldCall = useSelector(selectHeldCall)
  const queueHoldMedia = useSelector(selectQueueHeldCall)
  const parked = useSelector(getParkedMedia)
  const region = useSelector(selectMyRegion)
  const assistants = useSelector(selectCallAssisantMedia)

  const jitsi = useSelector(getJitsi)

  const isSoftphoneEnabled = useIsSoftphoneEnabled()
  const webrtcCall = useSelector(selectCall)
  const keypadPlacementRef = useRef<HTMLButtonElement>(null)

  let otherCall: Media | null = null

  const dispatch = useDispatch()

  useEffect((() => {
    if (call.clidObjectType !== ClidObjectType.extension || call.clidObjectType === ClidObjectType.extension && clidExtensionDetails) return

    dispatch(actions.fetchExtensionDetails.request({ id: call.clidObjectKey, server: call.server }))
  }), [dispatch, extension, clidExtensionDetails, call.clidObjectType, call.clidObjectKey, call.server])

  useEffect((() => {
    if (myId === null || call.clidObjectType !== ClidObjectType.extension || call.clidObjectType === ClidObjectType.extension && myExtensionDetails) return

    dispatch(actions.fetchExtensionDetails.request(myId))
  }), [dispatch, extension, clidExtensionDetails, call.clidObjectType, call.clidObjectKey, call.server, myExtensionDetails, myId])

  const isWebRtcCallActive = useCallback(() => {
    const result = isSoftphoneEnabled && webrtcCall && (webrtcCall.status === 'connected' || webrtcCall.status === 'reconnected')
    return result
  }, [isSoftphoneEnabled, webrtcCall])

  function onDigit(digit: string) {
    if (webrtcCall.canSendDtmf) {
      dispatch(webrtcActions.dtmf(digit))
    }
  }

  if (devices) {
    for (let i = 0; i < calls.length; i++) {
      if (otherCall === null && calls[i].id !== call.id) {
        otherCall = calls[i];
      }
    }
  }

  if (!me || !extension || !myId || !devices || !region || !dispatch || !devices || !assistants) { return null }

  if (call.status === CallState.None) return <MediaControl className={className}></MediaControl>

  const callCount = activeCallCount(calls, devices.devices)

  const callDevice = getActiveDevice(call, devices.devices, devices.primaryDevice)

  let control = GetCallControl(call, heldCall, callDevice, callCount, queueHoldMedia, parked, assistants.find(a => a.id === call.id) !== undefined,
    {
      access,
      mobile: me.mobile,
      associate: me.associate,
      status: extension.status,
      canReceiveVoicemail: me.canReceiveVoicemail,
      devices: devices.devices,
    })

  let inSupervisedTransfer = control.cancelTransfer.enabled

  const deviceText = getCallDevice(call.devices && call.devices.length > 1 ? call.devices[0].baseType : call.deviceType)
  const answerText = call.devices && call.devices.length > 1 ? deviceText : 'Answer'
  const answerTitle = 'Answer ' + deviceText;
  const answerItems = call.devices && call.devices.length > 1 ? call.devices.filter(d => d.baseType.toString() !== callDevice.baseType.toString()).map(d => <MenuItem key={d.number} onClick={() => {
    const dv = devices.devices.find(x => x.number === d.number)
    dispatch(actions.ccAnswerMedia(dv, call))
  }} >
    <CallDeviceIcon device={d} />
    {getCallDeviceDescription(d)}
  </MenuItem>) : []

  let sendToText = ""
  let sendToIcon = <ForwardToVoicemailIcon />
  let sendToHandler = null
  let sendToItems: JSX.Element[] = new Array(0)

  if (control.forwardToVoiceMail.visible && control.forwardToVoiceMail.enabled) {
    sendToText = "Send to Voicemail"
    sendToIcon = <ForwardToVoicemailIcon />

    const mailbox = call.extensionId !== myId.id ? call.extensionId : myId.id
    sendToHandler = function () { Phone.sendToVoicemail(callDevice.number, mailbox, call.id, true, call.extensionId !== myId.id) }
  }

  if (control.forwardToMobile.visible && control.forwardToMobile.enabled) {
    if (sendToHandler !== null) {
      sendToItems.push(<MenuItem key='tt-send-to-mobile' onClick={() => Phone.sendToMobile(callDevice.number)} ><ForwardToToMobileIcon /> Send to Mobile</MenuItem>)
    } else {
      sendToText = "Send to Mobile"
      sendToIcon = <ForwardToToMobileIcon />
      sendToHandler = function () { Phone.sendToMobile(callDevice.number) }
    }
  }

  if (control.forwardToAssociate.visible && control.forwardToAssociate.enabled) {
    if (sendToItems !== null) {
      sendToItems.push(<MenuItem key='tt-send-to-assoicate' onClick={() => Phone.sendToAssociate(callDevice.number)} ><ForwardToToAssociateIcon /> Send to Assoicate</MenuItem>)
    } else {
      sendToText = "Send to Assoicate"
      sendToIcon = <ForwardToToAssociateIcon />
      sendToHandler = function () { Phone.sendToAssociate(callDevice.number) }
    }
  }

  const items = [

    control.answer.visible && control.answer.enabled && answerItems.length === 0 && <button title={answerTitle} key="cc-answer" onClick={() => dispatch(actions.ccAnswerMedia(callDevice, call))} ><AnswerIcon /> {answerText}</button>,
    control.answer.visible && control.answer.enabled && answerItems.length > 0 && <SplitButton title={answerTitle} key="cc-answer-drop" onClick={() => dispatch(actions.ccAnswerMedia(callDevice, call))} items={answerItems}><AnswerIcon /> {answerText}</SplitButton>,

    control.hangup.visible && control.hangup.enabled && <button title="Hangup" key="cc-hangup" onClick={() => dispatch(actions.ccHangUp(callDevice.number, call))}><HangupIcon /></button>,
    control.hold.visible && control.hold.enabled && <button title="Hold" key="cc-hold" onClick={() => Phone.hold(callDevice.number)}><HoldIcon /></button>,
    control.resume.visible && control.resume.enabled && <button title="Resume" key="cc-resume" onClick={() => Phone.resume(callDevice.number)}><ResumeIcon /></button>,
    call.status === CallState.ExtensionAnswered && isWebRtcCallActive() && !webrtcCall.isMuted && <button title="Mute" key="cc-mute" onClick={() => dispatch(webrtcActions.muteMicrophone(true))}><PhoneMicrophoneIcon /></button>,
    call.status === CallState.ExtensionAnswered && isWebRtcCallActive() && webrtcCall.isMuted && <button title="Unmute" key="cc-unmute" onClick={() => dispatch(webrtcActions.muteMicrophone(false))}><PhoneMicrophoneMutedIcon /></button>,
    control.hold.visible && control.hold.enabled && access.features.phoneHoldMessage && <button title="Hold for regulatory message" key="cc-hold-message" onClick={() => Phone.holdMessage(callDevice.number, -1)}><HoldMessageIcon /></button>,
    control.blindTransfer.visible && control.blindTransfer.enabled && <button title="Blind Transfer" key="cc-blind" onClick={() =>

      dispatch(actions.openSmartSearch('blind-transfer',
        extension.status,
        extension.subStatus,
        callDevice.number,
        inSupervisedTransfer,
        call))

    }><BlindTransferIcon /></button>,
    control.supervisedTransfer.visible && control.supervisedTransfer.enabled && <button title="Supervised Transfer" key="cc-sup" onClick={() =>

      dispatch(actions.openSmartSearch('supervised-transfer',
        extension.status,
        extension.subStatus,
        callDevice.number,
        inSupervisedTransfer,
        call))

    }><SupervisedTransferIcon /></button>,
    control.cancelTransfer.visible && control.cancelTransfer.enabled && <button title="Cancel Transfer" key="cc-cancel" onClick={() => Phone.cancelTransfer(callDevice.number)}><CancelTransferIcon /></button>,
    control.completeTransfer.visible && control.completeTransfer.enabled && <button title="Complete Transfer" key="cc-complete" onClick={() => Phone.completeTransfer(callDevice.number)}><CompleteTransferIcon /></button>,

    sendToItems !== null && sendToHandler !== null && sendToItems.length === 0 && <button title={sendToText} key="cc-send-to" onClick={sendToHandler} >{sendToIcon} {sendToText}</button>,
    sendToItems !== null && sendToHandler !== null && sendToItems.length > 0 && <SplitButton title={sendToText} key="cc-send-to" onClick={sendToHandler} items={sendToItems}>{sendToIcon} {sendToText}</SplitButton>,

    control.sendToMobile.visible && control.sendToMobile.enabled && <button title="Send to Mobile" key="cc-send-mobile" onClick={() => Phone.sendToMobile(callDevice.number)}><ForwardToToMobileIcon /></button>,

    control.retrieveFromVoiceMail.visible && control.retrieveFromVoiceMail.enabled && <button title="Retrieve from Voicemail" key="cc-retrive-vm" onClick={() => Phone.retrieveVoicemail(callDevice.number, call.id)}><RetrieveVoicemailIcon /></button>,
    control.listenToVoicemail.visible && control.listenToVoicemail.enabled && <button title="Listen to Call in Voicemail" key="cc-listen-vm" onClick={() =>
      Phone.monitorVoicemail(callDevice.number, call.deviceId)
    }><ListenVoicemailIcon /></button>,
    control.holdAndRetrive.visible && control.holdAndRetrive.enabled && <button title="Hold and Retrieve" key="cc-hold-retrieve" onClick={() => {
      //need to hold active call which should not be this call
      if (otherCall !== null) {
        dispatch(actions.setQueueHoldKey(otherCall.id))
        dispatch(actions.ccHoldAndRetrieve(callDevice.number, otherCall.id, call.id, myId))
      }
    }
    }><HoldAndRetirveIcon /></button>,

    control.record.visible && control.record.enabled && call.mediaType !== 'VM' && <button title="Record Call" key="cc-recording" onClick={() => {
      Phone.startRecording(callDevice.number, myId.id)
      dispatch(actions.startRecording(call.id, true))
    }
    }><RecordIcon /></button>,
    control.stopRecording.visible && control.stopRecording.enabled && call.mediaType !== 'VM' && <button title="Stop Call Recording" key="cc-stop-recording" onClick={() =>
      Phone.stopRecording(callDevice.number, myId.id, call.voicemailPort)
    }><StopRecordIcon /></button>,
    control.suppressRecording.visible && control.suppressRecording.enabled && <button title="Suppress Call Recording" key="cc-suppress-recording" onClick={() => {
      Phone.suppressRecording(callDevice.number, call.voicemailPort, call.id)
      dispatch(actions.setMediaRecordingSuppress(call.id, call.voicemailPortIsConnected ? "recordingSuppressed" : "suppressed"))
    }
    }><SuppressRecordIcon /></button>,
    control.allowRecording.visible && control.allowRecording.enabled && <button title="Allow Call Recording" key="cc-allow-recording" onClick={() => {
      const record = call.voicemailPortIsSuppresed === "recordingSuppressed"
      //allow recording
      Phone.resumeRecording(callDevice.number, call.id)
      // if we were recording then start recording again

      if (record) {
        Phone.startRecording(callDevice.number, myId.id)
        dispatch(actions.startRecording(call.id, true))
      }
      //set data
      dispatch(actions.setMediaRecordingSuppress(call.id, "allowed"))

    }
    }><AllowRecordIcon /></button>,
    control.queueHold.visible && control.queueHold.enabled && <button title="Park" key="cc-park" onClick={() => dispatch(actions.ccQueueHold(callDevice.number, '', call.id, call.mediaType))}><ParkIcon /></button>,
    control.holdFor.visible && control.holdFor.enabled && <button title="Park for..." key="cc-park-for" onClick={() => {
      dispatch(actions.openSmartSearch('hold-for',
        extension.status,
        extension.subStatus,
        callDevice.number,
        inSupervisedTransfer,
        call,
        undefined))
    }}><ParkForIcon /></button>,
    control.queueResume.visible && control.queueResume.enabled && <button title="Unpark" key="cc-queue-resume" onClick={() =>
      dispatch(actions.ccQueueResume(myId.id, [call]))
    }><QueueResumeIcon /></button>,
  ]

  const dtmf = [
    <button title="Key 1" key="dtmf-1" onClick={() => onDigit('1')}><Dtmf1Icon /></button>,
    <button title="Key 2" key="dtmf-2" onClick={() => onDigit('2')}><Dtmf2Icon /></button>,
    <button title="Key 3" key="dtmf-3" onClick={() => onDigit('3')}><Dtmf3Icon /></button>,
    <button title="Key 4" key="dtmf-4" onClick={() => onDigit('4')}><Dtmf4Icon /></button>,
    <button title="Key 5" key="dtmf-5" onClick={() => onDigit('5')}><Dtmf5Icon /></button>,
    <button title="Key 6" key="dtmf-6" onClick={() => onDigit('6')}><Dtmf6Icon /></button>,
    <button title="Key 7" key="dtmf-7" onClick={() => onDigit('7')}><Dtmf7Icon /></button>,
    <button title="Key 8" key="dtmf-8" onClick={() => onDigit('8')}><Dtmf8Icon /></button>,
    <button title="Key 9" key="dtmf-9" onClick={() => onDigit('9')}><Dtmf9Icon /></button>,
    <button title="Key 0" key="dtmf-0" onClick={() => onDigit('0')}><Dtmf0Icon /></button>,
    <button title="Key star" key="dtmf-star" onClick={() => onDigit('*')}><DtmfStarIcon /></button>,
    <button title="Key hash" key="dtmf-hash" onClick={() => onDigit('#')}><DtmfHarshIcon /></button>,
  ]

  return <MediaControl className={className}>
    {call.dtmf && control.answer.visible && control.answer.enabled && answerItems.length === 0 && <button title={answerTitle} key="cc-answer" onClick={() => dispatch(actions.ccAnswerMedia(callDevice, call))} ><AnswerIcon /></button>}
    {call.dtmf && control.answer.visible && control.answer.enabled && answerItems.length > 0 && <SplitButton title={answerTitle} key="cc-answer-drop" onClick={() => dispatch(actions.ccAnswerMedia(callDevice, call))} items={answerItems}><AnswerIcon /></SplitButton>}
    {call.dtmf && control.hangup.visible && control.hangup.enabled && <button title="Hangup" key="cc-hangup" onClick={() => {
      dispatch(actions.ccHangUp(callDevice.number, call))
      dispatch(actions.updateMediaShowDtmf(call.id, false))
    }}><HangupIcon /></button>}
    {!call.dtmf && items}
    {call.dtmf && dtmf}
    {((call.deviceId !== call.extensionId && call.status === CallState.ExtensionPending) || (call.status === CallState.ExtensionAnswered && isWebRtcCallActive())) && <button title="Keypad" key="cc-keypad" onClick={() => dispatch(actions.updateMediaShowDtmf(call.id, !call.dtmf))} ref={keypadPlacementRef}><PhoneKeypadIcon /></button>}
    {!call.dtmf && call.status === CallState.ExtensionAnswered && myExtensionDetails && clidExtensionDetails && jitsi.id === '' && <button title="Video Conference" key="cc-video-conferecne" onClick={() => dispatch(actions.ccCreateVideoConferenceFromCall(call.id))}><ConferenceIcon /></button>}
  </MediaControl>
}
